import { Library, Check } from "lucide-react";
import { CONDITION_ABBR, LABELS } from "../config/variants";
import { isCustomVariant } from "../lib/variants";
import { getCollectionKey } from "../lib/collection";
import { tileTrendPrices, eur } from "../lib/prices";
import { ballForVariant } from "../lib/balls";
import { rarityInfo } from "../lib/rarity";
import { CardImage } from "./CardImage";

// Couleur par niveau de prix : neutre pour normale/prix unique, une teinte
// par niveau secondaire pour les distinguer d'un coup d'œil sur la grille —
// même palette que le tableau de prix de la fiche carte (CardDetailModal).
const PRICE_TILE_STYLE = {
  single:       { color: "#e4e4e7", background: "rgba(24,24,27,0.8)",   borderColor: "rgba(63,63,70,0.6)" },
  normal:       { color: "#e4e4e7", background: "rgba(24,24,27,0.8)",   borderColor: "rgba(63,63,70,0.6)" },
  reverse:      { color: "#7dd3fc", background: "rgba(12,74,110,0.55)", borderColor: "rgba(56,189,248,0.5)" },
  firstEdition: { color: "#fcd34d", background: "rgba(120,53,15,0.55)", borderColor: "rgba(251,191,36,0.5)" },
  holo:         { color: "#d8b4fe", background: "rgba(88,28,135,0.55)", borderColor: "rgba(192,132,252,0.5)" },
  cosmos:       { color: "#e879f9", background: "rgba(112,26,117,0.55)", borderColor: "rgba(232,121,249,0.5)" },
};
const PRICE_TILE_TITLE = {
  single: "Prix tendance Cardmarket (via TCGdex)",
  normal: "Prix tendance Cardmarket — Normale",
  reverse: "Prix tendance Cardmarket — Reverse",
  firstEdition: "Prix tendance Cardmarket — Première Édition",
  holo: "Prix tendance Cardmarket — Holo",
  cosmos: "Prix tendance Cardmarket — Holo Cosmos",
  rgb: "Prix tendance Cardmarket — RGB",
};

// Refonte du 2026-09-26 (maquettes validées par l'utilisateur) :
//  - les variantes sont des Poké Balls empilées en cascade à DROITE de
//    l'image (40 px, chevauchement 40 %) : grisée = manquante, couleur +
//    halo bleu = possédée, grisée + halo rouge = recherchée ;
//  - la carte entière passe en gris tant qu'aucune variante n'est possédée ;
//  - la rareté s'affiche dans la ligne de titre (numéro coloré + symbole
//    officiel, nom de la rareté en dessous) ;
//  - le prix passe en haut à GAUCHE, l'état (grade) en haut à droite.
export const PokemonCardTile = ({
  card, collectionMap, conditionMap = {}, onCardClick, onVariantClick, cardVariants,
  selectMode = false, selected = false, onToggleSelect, showPrice = true,
}) => {
  const variants = cardVariants || [{ key: "v-normal", type: "normal", label: "Normale" }];
  const prices = showPrice && !selectMode ? tileTrendPrices(card, variants) : [];
  const rarity = rarityInfo(card.rarity);

  const statusOf = (v) => collectionMap[getCollectionKey(card.id, v.type)];
  const ownedAny = variants.some(v => !v.loading && statusOf(v) === "owned");

  // État à afficher en tuile : celui de la première variante possédée qui a
  // un état renseigné (une carte peut avoir plusieurs variantes possédées).
  const ownedCondition = (() => {
    for (const v of variants) {
      if (v.loading) continue;
      const key = getCollectionKey(card.id, v.type);
      if (collectionMap[key] === "owned" && conditionMap[key]) return conditionMap[key];
    }
    return null;
  })();

  const handleTileClick = () => {
    if (selectMode) onToggleSelect(card.id);
    else onCardClick(card);
  };

  return (
    <div className={`pokemon-card group ${selectMode && selected ? "ring-2 ring-blue-400 rounded-lg" : ""}`}>
      {/* Image cliquable */}
      <div className={`relative overflow-hidden cursor-pointer card-art ${ownedAny ? "" : "card-art-ghost"}`}
        style={{aspectRatio:"2.5/3.5"}} onClick={handleTileClick}>
        {card.image ? (
          <CardImage card={card} quality="low"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 card-art-img" />
        ) : (
          <div className="w-full h-full bg-zinc-800 flex flex-col items-center justify-center gap-1.5 p-2 text-center"
            title="TCGdex n'a pas d'image pour cette carte">
            <Library className="w-8 h-8 text-zinc-600" />
            <span className="text-[10px] text-zinc-600 leading-tight">Image indisponible</span>
          </div>
        )}

        {/* Prix tendance Cardmarket, en haut à GAUCHE (la colonne de droite
            est réservée aux balls). 2 pastilles empilées quand normal et
            reverse/holo/1re éd. ont des cotes différentes. */}
        {prices.length > 0 && (
          <div className="absolute top-2 left-2 flex flex-col items-start gap-1 card-price">
            {prices.map(p => {
              const style = PRICE_TILE_STYLE[p.key] || PRICE_TILE_STYLE.single;
              return (
                <span
                  key={p.key}
                  className="px-1.5 py-0.5 rounded-md text-[11px] font-semibold border"
                  style={{ backdropFilter: "blur(4px)", ...style }}
                  title={PRICE_TILE_TITLE[p.key] || PRICE_TILE_TITLE.single}
                >
                  {eur(p.value)}
                </span>
              );
            })}
          </div>
        )}

        {/* État (grade) de la carte possédée, en haut à droite */}
        {ownedCondition && !selectMode && (
          <span
            className="absolute top-2 right-2 px-1.5 py-0.5 rounded-md text-xs font-bold"
            style={{ color: "#fff", background: "rgba(16,185,129,0.9)", backdropFilter: "blur(4px)" }}
            title={`État : ${ownedCondition}`}
          >
            {CONDITION_ABBR[ownedCondition] || ownedCondition}
          </span>
        )}

        {/* Mode sélection : pastille en haut à droite */}
        {selectMode && (
          <div
            className="absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center border-2"
            style={{
              background: selected ? "#3b82f6" : "rgba(24,24,27,0.75)",
              borderColor: selected ? "#3b82f6" : "#71717a",
              backdropFilter: "blur(4px)",
            }}
          >
            {selected && <Check className="w-4 h-4 text-white" />}
          </div>
        )}

        {/* Balls de variantes — cascade en bas à droite (désactivées en mode sélection) */}
        <div
          className="ball-cascade"
          style={selectMode ? { pointerEvents: "none", opacity: 0.6 } : undefined}
          onClick={e => e.stopPropagation()}
        >
          {variants.map(v => {
            if (v.loading) {
              return <span key={v.key} className="ball ball-loading" title="Chargement des variantes…">…</span>;
            }
            const isCustom = isCustomVariant(v.type);
            const ball = ballForVariant(v.type, isCustom ? v.type : undefined, rarity.tier, card.rarity);
            const status = statusOf(v);
            const state = status === "owned" ? "own" : status === "wanted" ? "want" : "none";
            const label = isCustom ? v.label : (LABELS[v.type] || v.type);
            return (
              <button
                key={v.key}
                type="button"
                className={`ball ball-${state}`}
                title={`${label} — ${ball.name}${status === "owned" ? " ✓ Possédée" : status === "wanted" ? " ★ Recherchée" : ""} · Clic : posséder · Maj+clic : rechercher`}
                onClick={(e) => {
                  e.stopPropagation();
                  onVariantClick(card.id, v.type, status, e.shiftKey);
                }}
              >
                <img src={ball.src} alt={label} draggable="false" />
                {isCustom && <span className="ball-abbr" style={{ background: v.custom?.color || "#52525b" }}>{v.custom?.abbr || "?"}</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Nom + numéro coloré par la rareté, rareté en toutes lettres dessous */}
      <div className="px-2 py-2 cursor-pointer" onClick={handleTileClick}>
        <div className="flex items-baseline justify-between gap-2">
          <h3 className="text-sm font-semibold text-white truncate" title={card.name}>{card.name}</h3>
          <span className="text-xs font-bold shrink-0 inline-flex items-center gap-0.5" style={{ color: rarity.color }}
            title={rarity.label || undefined}>
            {rarity.glyph && <span className="text-[11px]">{rarity.glyph}</span>}#{card.localId}
          </span>
        </div>
        {rarity.label && (
          <div className="rarity-line" style={{ color: rarity.color }}>{rarity.label}</div>
        )}
      </div>
    </div>
  );
};
